import { useFormContext } from "react-hook-form";
import { FieldValidator } from "./Form";
import { useContext, useEffect, useState } from "react";
import { checkEmail } from "../../services/publicService";
import { LangContext, LangContextType, LoadingContext, LoadingContextType } from "../../utilities/customHooks";
import textData from "../../textData.json";
import { Input } from "@components/ui/input";
import { cn } from "@lib/utils";
import { NumericFormat, PatternFormat } from "react-number-format";

export default function InputField(
  {
    name,
    placeholder,
    value,
    setValue,
    disabled,
    readOnly,
    validator,
    validate,
  }
  :
  {
    name: string,
    placeholder?: string,
    value: string | number,
    setValue: (value: string | number) => void,
    disabled?: boolean,
    readOnly?: boolean,
    validator: FieldValidator,
    validate?: (value: string) => string | undefined,
  }
) {
  const { langState } = useContext(LangContext) as LangContextType

  const { register, clearErrors, setError } = useFormContext();

  const {showLoading, hideLoading} = useContext(LoadingContext) as LoadingContextType;

  const fieldType = validator.fieldType;

  const [showPassword, setShowPassword] = useState(false);

  // solution to the issue where react-form-hook always show required error on pre-populated fields
  useEffect(() => {
    if (fieldType === "tel") {
      // Tel inputs aren't RHF-wired (PatternFormat stores raw digits via parent
      // state), so RHF's own value is always empty and a `pattern` rule would
      // never run. Validate the raw external value via a closure instead —
      // re-registered on every value change, so it can't go stale.
      const pat = validator.pattern instanceof RegExp ? validator.pattern : validator.pattern?.value;
      const msg = validator.pattern instanceof RegExp ? undefined : validator.pattern?.message;
      register(name, {
        required: validator.required,
        validate: () => {
          const raw = String(value ?? "");
          if (!raw) return true; // emptiness is `required`'s job — optional fields pass
          return !pat || pat.test(raw) || (msg ?? "Invalid value");
        }
      });
    } else if (fieldType === "number") {
      // Number fields store raw digits and NumericFormat already constrains input — skip pattern validation
      register(name, { required: validator.required });
    } else {
      register(name, validator);
    }
    // `|| value === 0` — a number field legitimately holding 0 must still get
    // its stale error cleared and its required rule relaxed like any other
    // pre-populated field (RHF's own value stays empty for non-wired inputs).
    if (value || value === 0) {
      clearErrors(`${name}`);
      register(`${name}`, { required: false });
    }
  }, [value]);

  const handleInputChange = (e: any) => { // Intentional any type
    const newValue = e.currentTarget.value;
    if (fieldType === "uniqueEmail") {
      clearErrors('duplicateEmail')
    }
    // Note: lowercase normalization for email fields is deferred to onBlur.
    // Transforming on every keystroke rewrites the controlled value mid-edit
    // and snaps the caret to the end of the input.
    setValue(newValue);
  }

  const handleEmailBlur = () => {
    if (typeof value === "string" && value !== value.toLocaleLowerCase()) {
      setValue(value.toLocaleLowerCase());
    }
    if (fieldType === "uniqueEmail") {
      checkDuplicateEmail(value);
    }
  }

  const checkDuplicateEmail = async (email: string | number) => {
    if (email) {
      showLoading();
      const response = await checkEmail((email as string).toLocaleLowerCase());
      hideLoading();
      if (!response.success) {
        setError('uniqueEmail', { type: 'custom', message: textData.Components.Form.InputField.EmailInUse[langState] });
      }
      else if (response.success) {
        clearErrors('duplicateEmail')
      }
    }
  }

  const inputClassName = cn(inputClasses, (disabled || readOnly) && disabledInputClasses);

  // Phone: (123) 456-7890 pattern format
  if (fieldType === "tel") {
    return (
      <div className="relative flex items-center">
        <PatternFormat
          format="(###) ###-####"
          mask="_"
          customInput={Input}
          value={String(value ?? "")}
          onValueChange={(values) => setValue(values.value)}
          placeholder={placeholder ?? "(123) 456-7890"}
          id={name}
          className={inputClassName}
          disabled={disabled || readOnly}
          readOnly={readOnly}
        />
      </div>
    );
  }

  // Number: comma-separated thousands
  if (fieldType === "number") {
    return (
      <div className="relative flex items-center">
        <NumericFormat
          thousandSeparator=","
          allowNegative={false}
          decimalScale={0}
          customInput={Input}
          value={value === "" || value === undefined ? "" : Number(value)}
          onValueChange={(values) => setValue(values.value)}
          placeholder={placeholder}
          id={name}
          className={inputClassName}
          disabled={disabled || readOnly}
          readOnly={readOnly}
          isAllowed={(values) => !values.value || values.value.length <= 15}
        />
      </div>
    );
  }

  // Default: text, email, password, url, etc.
  return (
    <div className="relative flex items-center">
      <Input
        disabled={disabled || readOnly}
        type={fieldType === "password" ? (showPassword ? "text" : fieldType) : fieldType}
        value={value}
        placeholder={placeholder}
        id={name}
        className={inputClassName}
        readOnly={readOnly}
        {
          ...register(name, {
            onChange: e => handleInputChange(e),
            validate: validate,
            ...(fieldType === "uniqueEmail" || fieldType === "email" ? { onBlur: handleEmailBlur } : {}),
          })
        }
      />
      {
        fieldType === "password" &&
        <button
          className="absolute right-[0.5rem] hover:cursor-pointer"
          onClick={ () => setShowPassword(!showPassword) }
        >
          <svg width="17" height="16" viewBox="0 0 17 16" fill="none" xmlns="http://www.w3.org/2000/svg">
          <g id="Show Icon" clipPath="url(#clip0_2274_6199)">
          <path id="Vector" d="M15.6204 7.13891C15.8099 7.37516 15.9148 7.6821 15.9148 8.00035C15.9148 8.3186 15.8099 8.62554 15.6204 8.86179C14.4204 10.3147 11.689 13.1432 8.50038 13.1432C5.31181 13.1432 2.58038 10.3147 1.38038 8.86179C1.19085 8.62554 1.08594 8.3186 1.08594 8.00035C1.08594 7.6821 1.19085 7.37516 1.38038 7.13891C2.58038 5.68603 5.31181 2.85742 8.50038 2.85742C11.689 2.85742 14.4204 5.68603 15.6204 7.13891Z" stroke="#74817B" strokeLinecap="round" strokeLinejoin="round"/>
          <path id="Vector_2" d="M8.50056 10.2853C9.76292 10.2853 10.7863 9.26195 10.7863 7.99958C10.7863 6.73722 9.76292 5.71387 8.50056 5.71387C7.23819 5.71387 6.21484 6.73722 6.21484 7.99958C6.21484 9.26195 7.23819 10.2853 8.50056 10.2853Z" stroke="#74817B" strokeLinecap="round" strokeLinejoin="round"/>
          </g>
          <defs>
          <clipPath id="clip0_2274_6199">
          <rect width="16" height="16" fill="white" transform="translate(0.5)"/>
          </clipPath>
          </defs>
          </svg>
        </button>
      }
    </div>
  )
}

export const inputClasses = "flex w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm"
export const disabledInputClasses = "!cursor-not-allowed"
